// API para comunicación con Elias AI a través de n8n
class ChatAPI {
  constructor() {
    this.baseURL = import.meta.env.VITE_API_URL || 'http://localhost:3000/api';
    // Webhook actualizado del agente conversacional Elias AI en n8n
    this.n8nWebhookUrl = 'https://inmobiliaria-ecomac.app.n8n.cloud/webhook/portal-gestion-tickets/agente-conversacional-elias-ai';
    // Deshabilitar completamente el modo mock para usar solo respuestas reales de n8n
    this.useMockMode = false;
  }

  // Simular respuestas de Elias para desarrollo
  getEliasResponse(message, attemptNumber) {
    const responses = {
      login: [
        'Entiendo que tienes problemas de login. ¿Has intentado verificar que tu usuario y contraseña sean correctos? También puedes probar limpiando el caché del navegador.',
        'Si el problema persiste, puede ser un problema con la sesión. ¿Has probado cerrar completamente el navegador y volver a intentar? También verifica que no tengas bloqueadores de cookies activos.'
      ],
      performance: [
        'Veo que experimentas lentitud en el sistema. Esto puede deberse a varios factores. ¿Podrías decirme en qué sección específica notas más lentitud? Mientras tanto, intenta refrescar la página con Ctrl+F5.',
        'Para problemas de rendimiento, también verifica tu conexión a internet y cierra otras pestañas que puedan consumir recursos. ¿El problema ocurre en todas las funciones o solo en algunas específicas?'
      ],
      error: [
        'Me hablas de un error en una funcionalidad. ¿Podrías ser más específico sobre qué error aparece? Si hay un mensaje de error, compártelo conmigo para poder ayudarte mejor.',
        'Los errores de funcionalidad pueden tener diferentes causas. ¿Habías usado esta función antes sin problemas? ¿El error aparece consistentemente o es intermitente?'
      ],
      integration: [
        'Los problemas de integración pueden ser complejos. ¿Qué sistema específico está fallando en la integración? ¿Recibes algún mensaje de error particular?',
        'Para problemas de integración, necesito más detalles. ¿La integración funcionaba antes? ¿Hay algún cambio reciente en configuración que puedas recordar?'
      ],
      general: [
        'Entiendo tu problema. Déjame ayudarte a encontrar una solución. ¿Podrías darme más detalles específicos sobre lo que está ocurriendo?',
        'Necesito un poco más de información para poder ayudarte efectivamente. ¿Cuándo comenzó este problema? ¿Hay algún patrón específico que hayas notado?'
      ]
    };

    // Determinar el tipo de problema basado en palabras clave
    let responseType = 'general';
    const lowerMessage = message.toLowerCase();
    
    if (lowerMessage.includes('login') || lowerMessage.includes('sesión') || lowerMessage.includes('iniciar')) {
      responseType = 'login';
    } else if (lowerMessage.includes('lento') || lowerMessage.includes('lentitud') || lowerMessage.includes('rendimiento')) {
      responseType = 'performance';
    } else if (lowerMessage.includes('error') || lowerMessage.includes('falla') || lowerMessage.includes('no funciona')) {
      responseType = 'error';
    } else if (lowerMessage.includes('integración') || lowerMessage.includes('conexión') || lowerMessage.includes('api')) {
      responseType = 'integration';
    }

    const responseArray = responses[responseType];
    const responseIndex = Math.min(attemptNumber - 1, responseArray.length - 1);
    
    return {
      message: responseArray[responseIndex],
      escalated: attemptNumber >= 2 && !this.isLikelyResolved(message),
      attemptNumber,
      type: responseType
    };
  }

  // Determinar si el problema probablemente se resolvió
  isLikelyResolved(message) {
    const positiveKeywords = ['gracias', 'resuelto', 'funcionó', 'perfecto', 'solucionado', 'ya funciona'];
    return positiveKeywords.some(keyword => message.toLowerCase().includes(keyword));
  }

  // Crear una nueva sesión
  async startSession() {
    try {
      // En producción, esto haría una llamada real al backend
      // const response = await fetch(`${this.baseURL}/chat/session`, {
      //   method: 'POST',
      //   headers: { 'Content-Type': 'application/json' },
      //   body: JSON.stringify({ userId: getUserId() })
      // });
      
      // Mock para desarrollo
      const sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      return sessionId;
    } catch (error) {
      console.error('Error starting chat session:', error);
      throw error;
    }
  }

  // Enviar mensaje completo con historial a Elias AI
  async sendMessageWithHistory(currentMessage, conversationHistory, sessionId, userData) {
    try {
      if (this.useMockMode) {
        // Modo mock para desarrollo
        await new Promise(resolve => setTimeout(resolve, 800));
        const attemptNumber = this.getAttemptNumber(sessionId);
        const response = this.getEliasResponse(currentMessage, attemptNumber);
        
        console.log('🤖 Respuesta de Elias (mock):', response);
        return response;
      } else {
        // Comunicación real con n8n - payload simplificado
        const payload = {
          // Mensaje actual del usuario
          currentMessage: currentMessage.trim(),
          
          // Historial completo de la conversación
          conversationHistory,
          
          // Información de la sesión
          sessionId,
          timestamp: new Date().toISOString(),
          
          // Datos del usuario autenticado
          user: {
            name: userData.name || userData.fullName || 'Usuario no identificado',
            email: userData.email || 'email@no-disponible.com',
            id: userData.id || userData.userId || 'ID-no-disponible'
          },
          
          // Metadatos
          source: 'portal-soporte',
          userAgent: navigator.userAgent
        };
        
        console.log('🌐 Enviando payload completo a n8n:', payload);
        
        const response = await fetch(this.n8nWebhookUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify(payload)
        });
        
        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(`HTTP error! status: ${response.status} - ${errorText.substring(0, 200)}`);
        }
        
        // Procesar respuesta
        const responseText = await response.text();
        let responseMessage = 'No se recibió respuesta del webhook.';
        
        try {
          const data = JSON.parse(responseText);
          if (Array.isArray(data) && data.length > 0 && data[0].output) {
            responseMessage = data[0].output;
          } else if (data.output) {
            responseMessage = data.output;
          } else if (data.message) {
            responseMessage = data.message;
          } else if (data.response) {
            responseMessage = data.response;
          } else if (data.text) {
            responseMessage = data.text;
          } else {
            responseMessage = JSON.stringify(data);
          }
        } catch (parseError) {
          // Si no es JSON, usar el texto directo
          responseMessage = responseText;
        }
        
        console.log('🤖 Respuesta de Elias AI:', responseMessage);
        
        return {
          message: responseMessage,
          escalated: false, // n8n maneja la escalación internamente
          type: 'general'
        };
      }
    } catch (error) {
      console.error('❌ Error sending message to Elias:', error);
      
      // Fallback a modo mock en caso de error
      const attemptNumber = this.getAttemptNumber(sessionId);
      const fallbackResponse = this.getEliasResponse(currentMessage, attemptNumber);
      
      return {
        ...fallbackResponse,
        error: true,
        errorMessage: 'Conexión con Elias AI temporalmente interrumpida. Usando respuesta de emergencia.'
      };
    }
  }

  // Método legacy mantenido por compatibilidad pero simplificado
  async sendMessage(message, sessionId, contextData = {}) {
    const userData = JSON.parse(localStorage.getItem('user') || '{}');
    const conversationHistory = contextData.conversationHistory || [];
    return this.sendMessageWithHistory(message, conversationHistory, sessionId, userData);
  }

  // Simular conteo de intentos por sesión
  getAttemptNumber(sessionId) {
    const attempts = JSON.parse(localStorage.getItem('chat_attempts') || '{}');
    attempts[sessionId] = (attempts[sessionId] || 0) + 1;
    localStorage.setItem('chat_attempts', JSON.stringify(attempts));
    return attempts[sessionId];
  }

  // Crear ticket desde conversación completa
  async createTicketFromConversation(userMessage, sessionId, conversationContext) {
    try {
      // Obtener datos del usuario
      const userData = JSON.parse(localStorage.getItem('user') || '{}');
      
      // Crear resumen de la conversación
      const conversationSummary = conversationContext
        .slice(-10) // Últimos 10 mensajes
        .map((msg, index) => `${index + 1}. ${msg.from === 'user' ? 'Usuario' : 'Elias AI'}: ${msg.text}`)
        .join('\n');
      
      // Webhook URL para escalación (crear ticket en ClickUp)
      const escalationWebhookUrl = 'https://inmobiliaria-ecomac.app.n8n.cloud/webhook/9846bace-b696-4c5a-a527-209d480bb887';
      
      const escalationPayload = {
        // Información del problema
        currentMessage: userMessage,
        sessionId,
        conversationSummary,
        escalationReason: 'Usuario persiste que el problema no se resolvió después de 2 intentos de solución',
        
        // Datos del usuario para el ticket
        userName: userData.name || userData.fullName || 'Usuario no identificado',
        userEmail: userData.email || 'email@no-disponible.com',
        userId: userData.id || userData.userId || 'ID-no-disponible',
        
        // Metadatos
        timestamp: new Date().toISOString(),
        source: 'elias-ai-escalation',
        priority: 'medium'
      };
      const response = await fetch(escalationWebhookUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(escalationPayload)
      });
      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ Error en escalación:', errorText);
        throw new Error(`Error al crear ticket: ${response.status} - ${errorText}`);
      }
      
      const responseText = await response.text();
      let ticketData;
      try {
        ticketData = JSON.parse(responseText);
      } catch (parseError) {
        console.error('❌ Error parseando respuesta de escalación:', parseError);
        // Crear respuesta mock si no se puede parsear
        ticketData = [{
          id: `TICK-${Date.now()}`,
          name: 'Ticket creado por escalación automática',
          url: 'https://app.clickup.com/t/ticket-id'
        }];
      }
      
      const ticketInfo = Array.isArray(ticketData) ? ticketData[0] : ticketData;
      return {
        success: true,
        ticketId: ticketInfo.id || ticketInfo.name || `TICK-${Date.now()}`,
        ticketUrl: ticketInfo.url || '#',
        message: `Comprendo tu frustración. He escalado tu caso al equipo técnico especializado. Se ha creado el ticket "${ticketInfo.name || 'Ticket de soporte'}" para darle seguimiento prioritario a tu problema. Nuestro equipo se pondrá en contacto contigo pronto.`,
        ticketData: ticketInfo
      };
    } catch (error) {
      console.error('❌ Error creating ticket from conversation:', error);
      
      // Fallback en caso de error
      return {
        success: false,
        ticketId: `FALLBACK-${Date.now()}`,
        message: 'He escalado tu caso al equipo técnico. Aunque hubo un problema técnico al generar el ticket automáticamente, tu solicitud ha sido registrada y nuestro equipo se pondrá en contacto contigo pronto.',
        error: error.message
      };
    }
  }

  // Simular creación de ticket en ClickUp (método legacy)
  async createTicketInClickUp(sessionId, lastMessage) {
    try {
      // En producción, esto activará el workflow de n8n para crear el ticket
      const ticketData = {
        title: `Escalación automática de chat - ${sessionId}`,
        description: `Ticket creado automáticamente desde el chat con Elias AI.\n\nÚltimo mensaje del cliente: "${lastMessage}"\n\nSesión: ${sessionId}`,
        priority: 'medium',
        assignee: 'Edward Lee', // PO
        tags: ['escalación-automática', 'elias-ai', 'chat'],
        source: 'chat-elias'
      };
      
      // Mock - en producción haría la llamada real a ClickUp
      return {
        success: true,
        ticketId: `TICK-${Date.now()}`,
        ticketData
      };
    } catch (error) {
      console.error('Error creating ticket:', error);
      throw error;
    }
  }

  // Obtener historial de chat
  async getChatHistory(sessionId) {
    try {
      // En producción, obtendría el historial del backend
      // const response = await fetch(`${this.baseURL}/chat/history/${sessionId}`);
      // return await response.json();
      
      // Mock para desarrollo
      return [];
    } catch (error) {
      console.error('Error getting chat history:', error);
      return [];
    }
  }

  // Cerrar sesión de chat
  async closeSession(sessionId) {
    try {
      // Limpiar intentos locales
      const attempts = JSON.parse(localStorage.getItem('chat_attempts') || '{}');
      delete attempts[sessionId];
      localStorage.setItem('chat_attempts', JSON.stringify(attempts));
      
      // En producción, notificaría al backend
      // await fetch(`${this.baseURL}/chat/session/${sessionId}/close`, {
      //   method: 'PUT'
      // });
      
      return { success: true };
    } catch (error) {
      console.error('Error closing chat session:', error);
      throw error;
    }
  }
}

// Exportar instancia singleton
export default new ChatAPI();
